import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { ProductOrganization } from '@/api/admin-products-types'
import { useUpdateProductOrganization } from '@/api/api-mutations'
import { useListProductCategories, useListProductGroups } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker } from '@/components/route-modal'
import { useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid/organization')({
	component: ProductOrganisation
})

function ProductOrganisation() {
	const { productUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => await api.getProductOrganization({ path: { productUuid } })}
		>
			{data => <ProductOrganisationComponent organization={data} />}
		</RouteModal>
	)
}

function ProductOrganisationComponent({ organization }: { organization: ProductOrganization }) {
	const { L } = useI18n()
	const { productUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const [groupSearch, setGroupSearch] = useState('')
	const debouncedGroupSearch = useDebounce(groupSearch)
	const groups = useListProductGroups({ q: debouncedGroupSearch, limit: 50 })
	const [categorySearch, setCategorySearch] = useState('')
	const debouncedCategorySearch = useDebounce(categorySearch)
	const categories = useListProductCategories({
		q: debouncedCategorySearch,
		limit: 50
	})

	const updateProductOrganization = useUpdateProductOrganization()
	const form = useAppForm({
		defaultValues: {
			groups: organization.groups?.map(g => g.uuid) || [],
			categories: organization.categories?.map(c => c.uuid) || []
		},
		onSubmit: async ({ value }) => {
			const result = await updateProductOrganization({
				path: { productUuid },
				body: { groups: value.groups, categories: value.categories }
			})
			if (result.isSuccess) {
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Product.EditHeader()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='groups'
							children={field => (
								<field.ComboboxField
									label={L.Fields.Groups()}
									multiple
									autoFocus
									optionsLoading={groups.isLoading}
									onSearchValueChange={setGroupSearch}
									options={
										groups.data?.groups.map(g => ({
											label: g.name,
											value: g.uuid
										})) ?? []
									}
								/>
							)}
						/>
						<form.AppField
							name='categories'
							children={field => (
								<field.ComboboxField
									label={L.Fields.Categories()}
									multiple
									optionsLoading={categories.isLoading}
									onSearchValueChange={setCategorySearch}
									options={
										categories.data?.categories?.map(c => ({
											label: c.name,
											value: c.uuid
										})) ?? []
									}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>

					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
